import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useAccessRequestSettingsQuery$data,
	useAccessRequestSettingsQuery$variables,
} from './__generated__/useAccessRequestSettingsQuery.graphql.ts';

export const ACCESS_REQUEST_SETTINGS_QUERY: GraphQLTaggedNode = graphql`
	query useAccessRequestSettingsQuery($cloudId: ID!) {
		confluence {
			siteConfiguration(cloudId: $cloudId) {
				contentAccessRequestSettings {
					enabled
					messageContent
				}
				spaceAccessRequestSettings {
					enabled
					messageContent
				}
			}
		}
	}
`;

export const useAccessRequestSettings = ({
	skip = false,
}: {
	/**
	 * Skips the query entirely. Callers outside the admin settings page use this to avoid issuing a
	 * request when the feature is gated off. Every returned value stays `undefined` while skipped.
	 */
	skip?: boolean;
} = {}): {
	spaceEnabled: boolean | null | undefined;
	contentEnabled: boolean | null | undefined;
	spaceMessageContent: string | null | undefined;
	contentMessageContent: string | null | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	refetch: () => void;
} => {
	const { cloudId } = useSessionData();

	const { data, loading, error, refetch } = useQueryInterop<
		useAccessRequestSettingsQuery$data,
		useAccessRequestSettingsQuery$variables
	>(ACCESS_REQUEST_SETTINGS_QUERY, {
		variables: { cloudId },
		skip,
	});

	const spaceEnabled = data?.confluence?.siteConfiguration?.spaceAccessRequestSettings?.enabled;
	const contentEnabled = data?.confluence?.siteConfiguration?.contentAccessRequestSettings?.enabled;
	const spaceMessageContent =
		data?.confluence?.siteConfiguration?.spaceAccessRequestSettings?.messageContent;
	const contentMessageContent =
		data?.confluence?.siteConfiguration?.contentAccessRequestSettings?.messageContent;

	return {
		spaceEnabled,
		contentEnabled,
		spaceMessageContent,
		contentMessageContent,
		loading,
		error,
		refetch,
	};
};
